import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConfirmService } from '../../shared/crud/confirm.service';
import { SessionService } from './session.service';

/** Must match `ExpectedClientCheck.HeaderName` in Core.Service. */
export const EXPECTED_CLIENT_HEADER = 'X-Expected-Client-Id';

/** Must match `ExpectedClientCheck.RefusalCode` in Core.Service. */
const REFUSAL_CODE = 'active_client_changed';

/** Marks the one replay after switching back, so a second refusal surfaces instead of looping. */
const RETRIED_HEADER = 'X-Retried-After-Client-Switch';

interface ClientChangedBody {
  code: string;
  expectedClientId: number;
  actualClientId: number;
  actualClientName?: string | null;
}

/**
 * Tells Core.Api which client this screen was built for, on every write, and handles the refusal when
 * the token has since moved to another one (card 253).
 *
 * <p><b>Why.</b> The API writes into the token's active client, and some payloads (menu settings)
 * carry no client at all. Each SPA token is now pinned to its own client, so this should be rare —
 * but a token minted before the pin existed, or an impersonation started on another computer, still
 * moves it underneath a screen. Without this, the save lands in the other client while the screen
 * still names this one.</p>
 *
 * <p><b>The refusal changes nothing</b> (Core.Api checks before the action runs), so the person is
 * offered the two honest ways on: switch this tab back to the client the screen shows and send the
 * same request again, or stay in the client the token moved to — reloading the screen there, which
 * discards what was typed.</p>
 *
 * <p>Not sent to `/api/session`: switching and impersonating ARE the screen and the token
 * disagreeing, and the API exempts them too.</p>
 */
export const expectedClientInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl) || isRead(req) || isSessionCall(req)) {
    return next(req);
  }

  const session = inject(SessionService);
  const conflicts = inject(ClientConflictService);

  const expected = session.session()?.activeClientId;
  if (!expected) {
    return next(req);
  }

  const sent = req.clone({ setHeaders: { [EXPECTED_CLIENT_HEADER]: String(expected) } });

  return next(sent).pipe(
    catchError((error: unknown) => {
      if (sent.headers.has(RETRIED_HEADER)) {
        return throwError(() => error);
      }

      return from(readRefusal(error)).pipe(
        switchMap((refusal) => {
          if (!refusal) {
            return throwError(() => error);
          }

          return from(conflicts.resolve(refusal)).pipe(
            switchMap((token) =>
              token
                ? next(
                    sent.clone({
                      setHeaders: { Authorization: `Bearer ${token}`, [RETRIED_HEADER]: '1' },
                    }),
                  )
                : throwError(() => error),
            ),
          );
        }),
      );
    }),
  );
};

/**
 * Asks the person what to do about a refused write, once per mismatch: several writes refused
 * together (a save that fans out) share the one question and the one answer.
 */
@Injectable({ providedIn: 'root' })
export class ClientConflictService {
  private readonly session = inject(SessionService);
  private readonly confirm = inject(ConfirmService);
  private readonly oidc = inject(OidcSecurityService);

  private pending: Promise<string | null> | null = null;

  /**
   * @returns the access token to replay the request with, once this tab is back on the client its
   *   screen was built for; null when nothing should be replayed.
   */
  resolve(refusal: ClientChangedBody): Promise<string | null> {
    this.pending ??= this.ask(refusal).finally(() => (this.pending = null));
    return this.pending;
  }

  private async ask(refusal: ClientChangedBody): Promise<string | null> {
    const current = this.session.session();
    const shown = current?.activeClientName || `client ${refusal.expectedClientId}`;
    const actual =
      refusal.actualClientName ||
      current?.accessibleClients?.find((c) => c.clientId === refusal.actualClientId)?.clientName ||
      `client ${refusal.actualClientId}`;

    const choice = await this.confirm.choose({
      title: 'Your client changed',
      message:
        `This page was loaded for ${shown}, but you are now working in ${actual} — ` +
        `probably switched in another tab or on another computer. Nothing was saved.`,
      primaryLabel: `Switch back to ${shown} and save`,
      secondaryLabel: `Stay in ${actual}`,
    });

    if (choice === 'primary') {
      await this.session.switchClient(refusal.expectedClientId);
      return (await firstValueFrom(this.oidc.getAccessToken())) || null;
    }

    if (choice === 'secondary') {
      // Reloading the session rebuilds the routed page in the new client (main-layout tracks the
      // active client), which is what discards the edits — the button says so by being red.
      await this.session.load();
    }

    return null;
  }
}

function isRead(req: HttpRequest<unknown>): boolean {
  return req.method === 'GET' || req.method === 'HEAD' || req.method === 'OPTIONS';
}

function isSessionCall(req: HttpRequest<unknown>): boolean {
  return new URL(req.url).pathname.toLowerCase().includes('/api/session');
}

/** The refusal's body, or null for any other error. The generated clients ask for blobs. */
async function readRefusal(error: unknown): Promise<ClientChangedBody | null> {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return null;
  }

  let body: unknown = error.error;
  try {
    if (body instanceof Blob) body = JSON.parse(await body.text());
    else if (typeof body === 'string') body = JSON.parse(body);
  } catch {
    return null;
  }

  const refusal = body as Partial<ClientChangedBody> | null;
  return refusal?.code === REFUSAL_CODE &&
    typeof refusal.expectedClientId === 'number' &&
    typeof refusal.actualClientId === 'number'
    ? (refusal as ClientChangedBody)
    : null;
}
